// File Location: src/components/policy/PolicyProfile.jsx

import React, { useState, useEffect } from "react";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Shield,
  Award,
  Edit,
  Save,
  X,
  Camera,
  BarChart3,
  Clock,
  CheckCircle,
  Target,
  TrendingUp,
  FileText,
  AlertCircle,
} from "lucide-react";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "../../services/firebaseConfig";
import { getPolicyWriterPerformanceMetrics } from "../../services/policyWriterService";
import { useAuth } from "../../context/AuthContext";

const PolicyProfile = () => {
  const {  userData, updateUserData } = useAuth();
  const user = {...userData, uid: auth?.currentUser?.uid} || {};
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    phone: "",
    designation: "",
    department: "",
    location: "",
    bio: "",
    specializations: [],
    certifications: [],
    profileImage: "",
  });

  useEffect(() => {
    if (userData) {
      setProfileData({
        name: userData.profile?.name || userData.email?.split("@")[0] || "",
        email: userData.email || "",
        phone: userData.profile?.phone || "",
        designation: userData.profile?.designation || "Policy Writer",
        department: userData.profile?.department || "Policy Verification",
        location: userData.profile?.location || "",
        bio: userData.profile?.bio || "",
        specializations: userData.profile?.specializations || [],
        certifications: userData.profile?.certifications || [],
        profileImage: userData.profile?.profileImage || "",
      });
      loadMetrics();
    }
  }, [userData]);

  const loadMetrics = async () => {
    if (!user?.uid) return;

    try {
      // Get last 30 days metrics
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);

      const result = await getPolicyWriterPerformanceMetrics(user.uid, {
        start: startDate.toISOString().split("T")[0],
        end: endDate.toISOString().split("T")[0],
      });

      if (result.success) {
        setMetrics(result.metrics);
      }
    } catch (error) {
      console.error("Error loading profile metrics:", error);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleArrayInputChange = (field, index, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? value : item)),
    }));
  };

  const addArrayItem = (field) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: [...prev[field], ""],
    }));
  };

  const removeArrayItem = (field, index) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type and size
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
  // 100MB limit for large documents
  alert("File size must be less than 100MB");
      return;
    }

    setUploading(true);
    try {
      const fileName = `profile_${user.uid}_${Date.now()}.${file.name
        .split(".")
        .pop()}`;
      const storageRef = ref(storage, `profile-images/${fileName}`);

      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      setProfileData((prev) => ({
        ...prev,
        profileImage: downloadURL,
      }));
    } catch (error) {
      console.error("Error uploading image:", error);
      alert("Failed to upload image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const userRef = doc(db, "users", user.uid);

      // Clean up empty strings from arrays
      const cleanedData = {
        ...profileData,
        specializations: profileData.specializations.filter(
          (s) => s.trim() !== ""
        ),
        certifications: profileData.certifications.filter(
          (c) => c.trim() !== ""
        ),
      };

      await updateDoc(userRef, {
        profile: cleanedData,
        updatedAt: new Date(),
      });

      // Update local user data
      await updateUserData();
      setIsEditing(false);
      alert("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("Failed to update profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset to original data
    if (userData) {
      setProfileData({
        name: userData.profile?.name || userData.email?.split("@")[0] || "",
        email: userData.email || "",
        phone: userData.profile?.phone || "",
        designation: userData.profile?.designation || "Policy Writer",
        department: userData.profile?.department || "Policy Verification",
        location: userData.profile?.location || "",
        bio: userData.profile?.bio || "",
        specializations: userData.profile?.specializations || [],
        certifications: userData.profile?.certifications || [],
        profileImage: userData.profile?.profileImage || "",
      });
    }
    setIsEditing(false);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const renderMetricsCard = () => {
    if (!metrics) return null;

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          Performance Overview (Last 30 Days)
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center">
            <div className="bg-blue-100 rounded-full p-3 w-fit mx-auto mb-2">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {metrics.totalReviewed}
            </div>
            <div className="text-sm text-gray-600">Total Reviews</div>
          </div>

          <div className="text-center">
            <div className="bg-green-100 rounded-full p-3 w-fit mx-auto mb-2">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {metrics.approvalRate}%
            </div>
            <div className="text-sm text-gray-600">Approval Rate</div>
          </div>

          <div className="text-center">
            <div className="bg-purple-100 rounded-full p-3 w-fit mx-auto mb-2">
              <Clock className="h-6 w-6 text-purple-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {metrics.averageReviewTime}m
            </div>
            <div className="text-sm text-gray-600">Avg Review Time</div>
          </div>

          <div className="text-center">
            <div className="bg-teal-100 rounded-full p-3 w-fit mx-auto mb-2">
              <TrendingUp className="h-6 w-6 text-teal-600" />
            </div>
            <div className="text-2xl font-bold text-gray-900">
              {metrics.avgDailyReviews}
            </div>
            <div className="text-sm text-gray-600">Daily Average</div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                Efficiency Score
              </span>
              <span className="text-sm text-gray-600">
                {metrics.averageReviewTime > 0
                  ? Math.max(10, 100 - metrics.averageReviewTime)
                  : 0}
                /100
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-teal-600 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${
                    metrics.averageReviewTime > 0
                      ? Math.max(10, 100 - metrics.averageReviewTime)
                      : 0
                  }%`,
                }}
              ></div>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                Quality Score
              </span>
              <span className="text-sm text-gray-600">
                {metrics.approvalRate}/100
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${metrics.approvalRate}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>
          <p className="text-gray-600">
            Manage your profile information and view performance metrics
          </p>
        </div>

        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Edit className="h-4 w-4" />
            <span>Edit Profile</span>
          </button>
        ) : (
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCancel}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <X className="h-4 w-4" />
              <span>Cancel</span>
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              <span>{loading ? "Saving..." : "Save Changes"}</span>
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Information */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Personal Information
            </h3>

            {/* Profile Image */}
            <div className="flex items-center space-x-6 mb-6">
              <div className="relative">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                  {profileData.profileImage ? (
                    <img
                      src={profileData.profileImage}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="h-12 w-12 text-gray-400" />
                  )}
                </div>
                {isEditing && (
                  <div className="absolute bottom-0 right-0">
                    <label className="bg-teal-600 text-white p-2 rounded-full cursor-pointer hover:bg-teal-700 transition-colors">
                      {uploading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <Camera className="h-4 w-4" />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                  </div>
                )}
              </div>
              <div>
                <h4 className="text-lg font-medium text-gray-900">
                  {profileData.name || "Policy Writer"}
                </h4>
                <p className="text-gray-600">{profileData.designation}</p>
                <p className="text-sm text-gray-500">
                  {profileData.department}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Enter your full name"
                  />
                ) : (
                  <p className="text-gray-900">
                    {profileData.name || "Not specified"}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <p className="text-gray-900">{profileData.email}</p>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Email cannot be changed
                </p>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Enter phone number"
                  />
                ) : (
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <p className="text-gray-900">
                      {profileData.phone || "Not specified"}
                    </p>
                  </div>
                )}
              </div>

              {/* Designation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Designation
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={profileData.designation}
                    onChange={(e) =>
                      handleInputChange("designation", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Enter your designation"
                  />
                ) : (
                  <p className="text-gray-900">{profileData.designation}</p>
                )}
              </div>

              {/* Department */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department
                </label>
                {isEditing ? (
                  <select
                    value={profileData.department}
                    onChange={(e) =>
                      handleInputChange("department", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="Policy Verification">
                      Policy Verification
                    </option>
                    <option value="Insurance Operations">
                      Insurance Operations
                    </option>
                    <option value="Claims Processing">Claims Processing</option>
                    <option value="Risk Assessment">Risk Assessment</option>
                    <option value="Quality Assurance">Quality Assurance</option>
                  </select>
                ) : (
                  <p className="text-gray-900">{profileData.department}</p>
                )}
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={profileData.location}
                    onChange={(e) =>
                      handleInputChange("location", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Enter your location"
                  />
                ) : (
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <p className="text-gray-900">
                      {profileData.location || "Not specified"}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Bio */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bio
              </label>
              {isEditing ? (
                <textarea
                  value={profileData.bio}
                  onChange={(e) => handleInputChange("bio", e.target.value)}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="Tell us about yourself and your experience..."
                />
              ) : (
                <p className="text-gray-900 whitespace-pre-wrap">
                  {profileData.bio || "No bio added yet."}
                </p>
              )}
            </div>

            {/* Specializations */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Specializations
              </label>
              {isEditing ? (
                <div className="space-y-2">
                  {profileData.specializations.map((spec, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={spec}
                        onChange={(e) =>
                          handleArrayInputChange(
                            "specializations",
                            index,
                            e.target.value
                          )
                        }
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="Enter specialization"
                      />
                      <button
                        onClick={() =>
                          removeArrayItem("specializations", index)
                        }
                        className="text-red-600 hover:text-red-800 p-1"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => addArrayItem("specializations")}
                    className="text-teal-600 hover:text-teal-800 text-sm font-medium"
                  >
                    + Add Specialization
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {profileData.specializations.length > 0 ? (
                    profileData.specializations.map((spec, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-teal-100 text-teal-800"
                      >
                        {spec}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-500">No specializations added</p>
                  )}
                </div>
              )}
            </div>

            {/* Certifications */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Certifications
              </label>
              {isEditing ? (
                <div className="space-y-2">
                  {profileData.certifications.map((cert, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={cert}
                        onChange={(e) =>
                          handleArrayInputChange(
                            "certifications",
                            index,
                            e.target.value
                          )
                        }
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="Enter certification"
                      />
                      <button
                        onClick={() => removeArrayItem("certifications", index)}
                        className="text-red-600 hover:text-red-800 p-1"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => addArrayItem("certifications")}
                    className="text-teal-600 hover:text-teal-800 text-sm font-medium"
                  >
                    + Add Certification
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {profileData.certifications.length > 0 ? (
                    profileData.certifications.map((cert, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Award className="h-4 w-4 text-yellow-600" />
                        <span className="text-gray-900">{cert}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500">No certifications added</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="lg:col-span-1 space-y-6">
          {renderMetricsCard()}

          {/* Account Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Account Information
            </h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600">User ID</span>
                </div>
                <span className="text-sm font-mono text-gray-900">
                  {user?.uid?.slice(0, 8)}...
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Shield className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600">Role</span>
                </div>
                <span className="text-sm text-gray-900">Policy Writer</span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600">Member Since</span>
                </div>
                <span className="text-sm text-gray-900">
                  {formatDate(userData?.createdAt)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-gray-600">Account Status</span>
                </div>
                <span className="text-sm font-medium text-green-600">
                  Active
                </span>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Quick Stats
            </h3>

            {metrics ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    Reviews This Month
                  </span>
                  <span className="text-lg font-bold text-teal-600">
                    {metrics.totalReviewed}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Emergency Cases</span>
                  <span className="text-lg font-bold text-red-600">
                    {metrics.emergencyReviews}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Complex Cases</span>
                  <span className="text-lg font-bold text-purple-600">
                    {metrics.complexCases}
                  </span>
                </div>

                <div className="pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">
                      Performance Score
                    </span>
                    <div className="text-right">
                      <span className="text-lg font-bold text-gray-900">
                        {Math.round(
                          (metrics.approvalRate +
                            Math.max(10, 100 - metrics.averageReviewTime)) /
                            2
                        )}
                      </span>
                      <span className="text-sm text-gray-500">/100</span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-gradient-to-r from-teal-500 to-green-500 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${Math.round(
                          (metrics.approvalRate +
                            Math.max(10, 100 - metrics.averageReviewTime)) /
                            2
                        )}%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <BarChart3 className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  Loading performance data...
                </p>
              </div>
            )}
          </div>

          {/* Security Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-blue-900">
                  Security Notice
                </h4>
                <p className="text-sm text-blue-700 mt-1">
                  Keep your profile information up to date for better security
                  and communication.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolicyProfile;
